import { useEffect, useMemo, useRef, useState, type CSSProperties, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";

import {
  fetchGroundedProvenance,
  type GroundedOpenAction,
  type GroundedProvenanceResult,
} from "../../api/groundedProvenance";
import { useTheme, type Theme } from "../../context/ThemeContext";
import {
  DagViewerSurface,
  type DagViewerCommandAction,
  type DagViewerCommandRequest,
  type DagViewerGroundedNavigationRequest,
  type DagViewerGroundingContext,
  type DagViewerNode,
} from "../dag-viewer";
import {
  applyLineageLabPlanToSearch,
  LINEAGE_LAB_FIXTURE_SIZES,
  LINEAGE_LAB_VIEWPORTS,
  planLineageLabCommand,
  type LineageLabAgentAction,
  type LineageLabCommandPlan,
  type LineageLabFixtureSize,
  type LineageLabViewport,
} from "./agentCommands";
import { GroundedProvenanceAnswer } from "./GroundedProvenanceAnswer";
import { groundedQuestionRequestsComparison, routeLineageWorkspaceInput } from "./groundedQuestion";
import "./lineage-workspace.css";

interface LineageWorkspaceSurfaceProps {
  projectId: string;
  projectName: string;
  branch: string;
  selectedNodeId?: string | null;
  onSelectNode?: (nodeId: string | null) => void;
  onActivateNode?: (nodeId: string, node?: DagViewerNode) => void;
}

interface CommandHistoryEntry {
  plan: LineageLabCommandPlan;
  searchBefore: string;
  themeBefore: Theme;
}

interface ViewportDefinition {
  id: LineageLabViewport;
  label: string;
  shortLabel: string;
  width: number | null;
  height: number | null;
}

const VIEWPORTS: readonly ViewportDefinition[] = [
  { id: "fill", label: "Fill available space", shortLabel: "Fill", width: null, height: null },
  { id: "1440x900", label: "Desktop · 1440 × 900", shortLabel: "Desktop", width: 1440, height: 900 },
  { id: "1280x720", label: "Laptop · 1280 × 720", shortLabel: "Laptop", width: 1280, height: 720 },
  { id: "1024x768", label: "Tablet · 1024 × 768", shortLabel: "Tablet", width: 1024, height: 768 },
  { id: "844x390", label: "Phone landscape · 844 × 390", shortLabel: "Phone", width: 844, height: 390 },
] as const;

const FIXTURE_SIZE_SET = new Set<number>(LINEAGE_LAB_FIXTURE_SIZES);
const VIEWPORT_SET = new Set<string>(LINEAGE_LAB_VIEWPORTS);
const numberFormatter = new Intl.NumberFormat();

const SUGGESTIONS = [
  "Show invalidated records",
  "Stress test 50k in Flow on a phone",
  "Make labels bigger and hide operation runs",
] as const;

function fixtureSizeFromParams(params: URLSearchParams): LineageLabFixtureSize | null {
  const requested = Number(params.get("labSize"));
  return FIXTURE_SIZE_SET.has(requested) ? requested as LineageLabFixtureSize : null;
}

function viewportFromParams(params: URLSearchParams): LineageLabViewport {
  const requested = params.get("labViewport");
  return requested && VIEWPORT_SET.has(requested) ? requested as LineageLabViewport : "fill";
}

function fixtureLabel(size: LineageLabFixtureSize | null): string {
  if (size == null) return "Project";
  if (size === 20) return "20-node sample";
  return `${numberFormatter.format(size)} fixture`;
}

function viewerActions(actions: readonly LineageLabAgentAction[]): DagViewerCommandAction[] {
  const result: DagViewerCommandAction[] = [];
  for (const action of actions) {
    switch (action.type) {
      case "set-search":
      case "set-filter":
      case "reset-filters":
      case "set-category-labels":
      case "set-label-scale":
      case "adjust-label-scale":
      case "set-category-visibility":
      case "show-all-categories":
      case "set-category-importance":
      case "move-category":
      case "run-benchmark":
        result.push(action);
        break;
      case "reset-lab":
        result.push({ type: "reset-viewer" });
        break;
      default:
        break;
    }
  }
  return result;
}

function searchFallbackPlan(source: string, plan: LineageLabCommandPlan): LineageLabCommandPlan {
  const words = source.trim().split(/\s+/).filter(Boolean);
  if (plan.actions.length > 0 || source.includes("?") || words.length > 6) return plan;
  return {
    ...plan,
    actions: [{ type: "set-search", query: source.trim(), isolateMatches: false }],
    requirements: [],
    ready: true,
    summary: `search for ${source.trim()}`,
  };
}

export function LineageWorkspaceSurface({
  projectId,
  projectName,
  branch,
  selectedNodeId = null,
  onSelectNode,
  onActivateNode,
}: LineageWorkspaceSurfaceProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { theme, toggleTheme } = useTheme();
  const [setupOpen, setSetupOpen] = useState(false);
  const [commandText, setCommandText] = useState("");
  const [receipt, setReceipt] = useState<LineageLabCommandPlan | null>(null);
  const [history, setHistory] = useState<CommandHistoryEntry[]>([]);
  const [commandRequest, setCommandRequest] = useState<DagViewerCommandRequest | null>(null);
  const [groundingContext, setGroundingContext] = useState<DagViewerGroundingContext | null>(null);
  const [groundedResult, setGroundedResult] = useState<GroundedProvenanceResult | null>(null);
  const [groundedError, setGroundedError] = useState<string | null>(null);
  const [groundedLoading, setGroundedLoading] = useState(false);
  const [groundedNavigationRequest, setGroundedNavigationRequest] = useState<DagViewerGroundedNavigationRequest | null>(null);
  const nextCommandId = useRef(1);
  const nextGroundedNavigationId = useRef(1);
  const groundedAbortController = useRef<AbortController | null>(null);
  const groundedProjectId = useRef(projectId);

  useEffect(() => () => groundedAbortController.current?.abort(), []);
  useEffect(() => {
    if (groundedProjectId.current === projectId) return;
    groundedProjectId.current = projectId;
    groundedAbortController.current?.abort();
    setGroundingContext(null);
    setGroundedResult(null);
    setGroundedError(null);
    setGroundedLoading(false);
    setGroundedNavigationRequest(null);
  }, [projectId]);

  const fixtureSize = fixtureSizeFromParams(searchParams);
  const viewportId = viewportFromParams(searchParams);
  const viewport = VIEWPORTS.find((candidate) => candidate.id === viewportId) ?? VIEWPORTS[0]!;
  const frameStyle = viewport.width && viewport.height
    ? ({ width: viewport.width, height: viewport.height } satisfies CSSProperties)
    : undefined;
  const sourceSummary = fixtureSize == null
    ? viewportId === "fill" ? "Project" : `Project · ${viewport.shortLabel}`
    : `${fixtureSize === 20 ? "Sample" : fixtureSize >= 1_000 ? `${fixtureSize / 1_000}k` : fixtureSize} · ${viewport.shortLabel}`;

  const issueViewerCommand = (actions: readonly DagViewerCommandAction[]) => {
    if (!actions.length) return;
    setCommandRequest({ id: nextCommandId.current++, actions: [...actions] });
  };

  const replaceParams = (mutate: (next: URLSearchParams) => void) => {
    const next = new URLSearchParams(searchParams);
    mutate(next);
    setSearchParams(next, { replace: true });
  };

  const chooseFixture = (size: LineageLabFixtureSize | null) => {
    replaceParams((next) => {
      if (size == null) next.delete("labSize");
      else next.set("labSize", String(size));
      next.delete("nodes");
      next.delete("dagA");
      next.delete("dagB");
    });
    setReceipt(null);
  };

  const chooseViewport = (viewport: LineageLabViewport) => {
    replaceParams((next) => next.set("labViewport", viewport));
  };

  const executePlan = (source: string) => {
    const trimmed = source.trim();
    if (!trimmed) return;
    const planned = planLineageLabCommand(trimmed, {
      compareA: searchParams.get("dagA"),
      compareB: searchParams.get("dagB"),
      selectedNodeIds: selectedNodeId ? [selectedNodeId] : [],
    });
    const plan = searchFallbackPlan(trimmed, planned);
    groundedAbortController.current?.abort();
    setGroundedLoading(false);
    setGroundedResult(null);
    setGroundedError(null);
    if (!plan.ready) {
      setReceipt(plan);
      setCommandText("");
      return;
    }
    const next = applyLineageLabPlanToSearch(searchParams, plan);
    if (plan.actions.some((action) => action.type === "set-fixture")) {
      next.delete("nodes");
      next.delete("dagA");
      next.delete("dagB");
    }
    setHistory((current) => [...current, { plan, searchBefore: searchParams.toString(), themeBefore: theme }]);
    setSearchParams(next, { replace: true });

    const requestedTheme = plan.actions.find((action): action is Extract<LineageLabAgentAction, { type: "set-theme" }> => action.type === "set-theme")?.theme;
    if (requestedTheme && requestedTheme !== theme) toggleTheme();
    issueViewerCommand(viewerActions(plan.actions));
    setReceipt(plan);
    setCommandText("");
  };

  const askGroundedQuestion = async (source: string) => {
    const question = source.trim();
    if (!question) return;
    groundedAbortController.current?.abort();
    const controller = new AbortController();
    groundedAbortController.current = controller;
    setReceipt(null);
    setGroundedResult(null);
    setGroundedError(null);
    setGroundedLoading(true);
    setCommandText("");
    try {
      const comparisonWindow = groundingContext?.comparisonWindow
        && groundedQuestionRequestsComparison(question)
        ? groundingContext.comparisonWindow
        : null;
      const result = await fetchGroundedProvenance({
        project_id: projectId,
        question,
        ...(groundingContext ? {
          root_ref: {
            entity_kind: "artifact_version" as const,
            id: groundingContext.rootRef.id,
          },
          recorded_as_of: groundingContext.cut.recordedAsOf,
          snapshot_token: groundingContext.cut.snapshotToken,
          ...(comparisonWindow ? {
            comparison: {
              before_as_of: comparisonWindow.beforeAsOf,
              after_as_of: comparisonWindow.afterAsOf,
            },
          } : { as_of: groundingContext.cut.effectiveAsOf }),
        } : {}),
      }, { signal: controller.signal });
      if (groundedAbortController.current !== controller) return;
      setGroundedResult(result.data);
    } catch (error) {
      if (controller.signal.aborted || groundedAbortController.current !== controller) return;
      setGroundedError(error instanceof Error ? error.message : "Grounded provenance is unavailable.");
    } finally {
      if (groundedAbortController.current === controller) setGroundedLoading(false);
    }
  };

  const executeInput = (source: string) => {
    if (routeLineageWorkspaceInput(source) === "grounded_question") {
      void askGroundedQuestion(source);
    } else {
      executePlan(source);
    }
  };

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    executeInput(commandText);
  };

  const openGroundedAction = (action: GroundedOpenAction) => {
    const document = groundedResult?.document;
    const toolCall = document?.tool_calls[action.tool_call_ordinal];
    if (!document || !toolCall) return;
    const sourceRequest = toolCall.source_request;
    const highlighted = action.highlight_citation_id === null
      ? null
      : document.citations.find((citation) => citation.citation_id === action.highlight_citation_id) ?? null;
    const highlight = highlighted ? {
      citationId: highlighted.citation_id,
      sourceLocator: highlighted.source_locator,
      recordKind: highlighted.record_kind,
      recordId: highlighted.record_id,
      secondaryRecordId: highlighted.secondary_record_id,
    } : null;
    const base = {
      id: nextGroundedNavigationId.current++,
      rootRef: { entityKind: "artifact_version" as const, id: action.root_ref.id },
      expectedSourceSemanticDigest: toolCall.source_semantic_digest,
      highlight,
    };

    if (sourceRequest.source_request_kind === "temporal_compare") {
      setGroundedNavigationRequest({
        ...base,
        targetView: "time_compare",
        sourceRequest,
        cut: {
          effectiveAsOf: sourceRequest.after_as_of,
          recordedAsOf: sourceRequest.recorded_as_of,
          snapshotToken: sourceRequest.snapshot_token,
        },
        comparisonWindow: { beforeAsOf: sourceRequest.before_as_of, afterAsOf: sourceRequest.after_as_of },
      });
      return;
    }
    const cut = {
      effectiveAsOf: sourceRequest.as_of,
      recordedAsOf: sourceRequest.recorded_as_of,
      snapshotToken: sourceRequest.snapshot_token,
    };
    if (sourceRequest.source_request_kind === "proof_trace") {
      setGroundedNavigationRequest({ ...base, targetView: "proof_trace", sourceRequest, cut, comparisonWindow: null });
    } else if (sourceRequest.source_request_kind === "impact_analysis") {
      setGroundedNavigationRequest({ ...base, targetView: "impact", sourceRequest, cut, comparisonWindow: null });
    } else {
      setGroundedNavigationRequest({ ...base, targetView: "human_provenance", sourceRequest, cut, comparisonWindow: null });
    }
  };

  const undoLastCommand = () => {
    const previous = history[history.length - 1];
    if (!previous) return;
    const remaining = history.slice(0, -1);
    setSearchParams(new URLSearchParams(previous.searchBefore), { replace: true });
    if (theme !== previous.themeBefore) toggleTheme();
    issueViewerCommand([
      { type: "reset-viewer" },
      ...remaining.flatMap((entry) => viewerActions(entry.plan.actions)),
    ]);
    setHistory(remaining);
    setReceipt(remaining[remaining.length - 1]?.plan ?? null);
  };

  const resetWorkspace = () => {
    const plan = planLineageLabCommand("reset lab");
    const next = applyLineageLabPlanToSearch(searchParams, plan);
    next.delete("nodes");
    setSearchParams(next, { replace: true });
    issueViewerCommand([{ type: "reset-viewer" }]);
    setHistory([]);
    setReceipt(null);
    groundedAbortController.current?.abort();
    setGroundedResult(null);
    setGroundedError(null);
    setGroundedLoading(false);
    setGroundedNavigationRequest(null);
    setCommandText("");
  };

  const leadingControl = (
    <div className="lineage-workspace__source">
      <button
        type="button"
        className={setupOpen ? "is-active" : ""}
        aria-expanded={setupOpen}
        aria-label={`Lineage source and test frame: ${sourceSummary}`}
        onClick={() => setSetupOpen((open) => !open)}
      >
        <i aria-hidden="true" />
        <span><small>Lineage</small><strong>{sourceSummary}</strong></span>
        <b aria-hidden="true">⌄</b>
      </button>
      {setupOpen ? (
        <aside className="lineage-workspace__setup" aria-label="Lineage source and frame">
          <header><strong>Source & test frame</strong><button type="button" onClick={() => setSetupOpen(false)} aria-label="Close source settings">×</button></header>
          <fieldset>
            <legend>Data</legend>
            <div>
              <button type="button" className={fixtureSize == null ? "is-active" : ""} onClick={() => chooseFixture(null)}>Project</button>
              {LINEAGE_LAB_FIXTURE_SIZES.map((size) => (
                <button type="button" className={fixtureSize === size ? "is-active" : ""} key={size} onClick={() => chooseFixture(size)}>
                  {size === 20 ? "Sample" : `${size / 1_000}k`}
                </button>
              ))}
            </div>
          </fieldset>
          <label>
            <span>Viewer frame</span>
            <select value={viewportId} onChange={(event) => chooseViewport(event.target.value as LineageLabViewport)}>
              {VIEWPORTS.map((candidate) => <option key={candidate.id} value={candidate.id}>{candidate.label}</option>)}
            </select>
          </label>
          <p>Uses Thread’s current theme. The validated scale ceiling is 50,000 records.</p>
        </aside>
      ) : null}
    </div>
  );

  const agentControl = (
    <form className="lineage-workspace__agent" role="search" onSubmit={onSubmit}>
      <span aria-hidden="true">✦</span>
      <input
        type="text"
        value={commandText}
        aria-label="Ask or find in lineage"
        placeholder="Ask or find lineage…"
        onChange={(event) => setCommandText(event.target.value)}
      />
      <button type="submit" disabled={!commandText.trim() || groundedLoading}>{groundedLoading ? "Asking…" : "Run"}</button>
      {receipt ? (
        <aside className={`lineage-workspace__receipt${receipt.requirements.length || receipt.caveats.length ? " is-noted" : ""}`} aria-live="polite">
          <button type="button" className="lineage-workspace__receipt-close" onClick={() => setReceipt(null)} aria-label="Dismiss lineage agent receipt">×</button>
          <span>{receipt.ready ? "Command applied" : "Command needs context"}</span>
          <strong>{receipt.ready ? receipt.summary : "Select an anchor to continue"}</strong>
          {receipt.requirements[0]
            ? <p>{receipt.requirements[0].message}</p>
            : receipt.caveats[0]
              ? <p>{receipt.caveats[0]}</p>
              : <p>The graph now reflects this instruction.</p>}
          <div className="lineage-workspace__receipt-actions">
            <button type="button" disabled={!history.length} onClick={undoLastCommand}>Undo</button>
            <button type="button" onClick={resetWorkspace}>Reset</button>
          </div>
          <div className="lineage-workspace__next-actions" aria-label="Suggested lineage commands">
            {SUGGESTIONS.slice(0, 2).map((suggestion) => <button type="button" key={suggestion} onClick={() => executePlan(suggestion)}>{suggestion}</button>)}
          </div>
        </aside>
      ) : null}
      {groundedResult ? (
        <GroundedProvenanceAnswer
          result={groundedResult}
          onDismiss={() => setGroundedResult(null)}
          onOpenAction={openGroundedAction}
        />
      ) : null}
      {groundedError ? (
        <aside className="lineage-workspace__grounded-error" role="alert">
          <button type="button" onClick={() => setGroundedError(null)} aria-label="Dismiss grounded provenance error">×</button>
          <span>Grounded answer unavailable</span>
          <p>{groundedError}</p>
        </aside>
      ) : null}
    </form>
  );

  const viewerKey = useMemo(() => `${projectId}:${fixtureSize ?? "project"}`, [fixtureSize, projectId]);

  return (
    <div className={`lineage-workspace${viewportId === "fill" ? " is-fill" : ""}`} data-testid="lineage-workspace">
      <section
        className="lineage-workspace__frame"
        style={frameStyle}
        aria-label={`${fixtureLabel(fixtureSize)} lineage at ${viewport.label}`}
        data-fixture-size={fixtureSize ?? "project"}
        data-viewport={viewportId}
      >
        <DagViewerSurface
          key={viewerKey}
          projectId={projectId}
          projectName={fixtureSize == null ? projectName : fixtureLabel(fixtureSize)}
          branch={branch}
          selectedNodeId={selectedNodeId}
          onSelectNode={onSelectNode}
          onActivateNode={onActivateNode}
          syntheticNodeCount={fixtureSize}
          enabled={fixtureSize == null}
          streamlined
          toolbarLeading={leadingControl}
          toolbarQuery={agentControl}
          commandRequest={commandRequest}
          onGroundingContextChange={setGroundingContext}
          groundedNavigationRequest={groundedNavigationRequest}
        />
      </section>
    </div>
  );
}
